import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from 'sonner';
import CommitmentSigner from '../components/CommitmentSigner';
import { FileText, CheckCircle } from 'lucide-react';

export default function Profile() {
  const { user, api } = useAuth();
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    bio: ''
  });
  const [loading, setLoading] = useState(false);
  const [commitmentUrl, setCommitmentUrl] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        bio: user.bio || ''
      });
      setCommitmentUrl(user.commitment_pdf_url);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/auth/me', formData);
      toast.success("Perfil actualizado");
    } catch (error) {
      toast.error("Error al actualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <h1 className="text-3xl font-bold font-heading">Mi Perfil</h1>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Mis Datos</TabsTrigger>
          <TabsTrigger value="commitment">Documentación Legal</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Información Personal</CardTitle>
              <CardDescription>Actualiza tus datos de contacto.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Correo Electrónico</Label>
                  <Input value={user?.email} disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>Nombre Completo</Label>
                  <Input 
                    value={formData.full_name} 
                    onChange={e => setFormData({...formData, full_name: e.target.value})} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input 
                    value={formData.phone} 
                    onChange={e => setFormData({...formData, phone: e.target.value})} 
                    placeholder="+52 ..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bio / Notas</Label>
                  <Input 
                    value={formData.bio} 
                    onChange={e => setFormData({...formData, bio: e.target.value})} 
                    placeholder="Algo sobre ti..."
                  />
                </div>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="commitment">
            {commitmentUrl ? (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-green-600 flex items-center gap-2">
                            <CheckCircle /> Documentación Verificada
                        </CardTitle>
                        <CardDescription>
                            Ya has firmado tu carta compromiso.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="p-4 bg-muted rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <FileText className="text-primary" />
                                <span className="font-medium">Carta Compromiso Firmada.pdf</span>
                            </div>
                            <a href={commitmentUrl} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline">Ver Documento</Button>
                            </a>
                        </div>
                        <p className="text-sm text-muted-foreground text-center">
                            Si necesitas actualizar tu documento, contacta a un administrador.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>Firma de Compromiso</CardTitle>
                        <CardDescription>
                            Para participar en las tandas, es necesario que firmes la carta compromiso y subas tu identificación.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <CommitmentSigner onSigned={(url) => setCommitmentUrl(url)} />
                    </CardContent>
                </Card>
            )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
